import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Handshake, Mail, FileDown } from "lucide-react";
import api from "@/lib/api";
import Reveal from "@/components/Reveal";
import { useUiTexts } from "@/lib/uiTexts";

export default function Partners() {
  const [partners, setPartners] = useState([]);
  const [mediaKit, setMediaKit] = useState(null);
  const texts = useUiTexts();

  useEffect(() => {
    api.get("/partners").then((r) => setPartners(r.data)).catch(() => {});
    api.get("/media-kit").then((r) => setMediaKit(r.data?.url ? r.data : null)).catch(() => {});
  }, []);

  return (
    <div data-testid="partners-page" className="mx-auto max-w-7xl px-4 py-12 sm:px-8 lg:py-16">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500">{texts.partners_label}</p>
      <h1 className="mt-2 text-2xl font-extrabold uppercase tracking-tight sm:text-3xl lg:text-4xl">{texts.partners_title}</h1>
      <p className="mt-3 max-w-2xl text-sm text-slate-400">{texts.partners_subtitle}</p>

      {partners.length === 0 ? (
        <p className="mt-10 text-slate-500" data-testid="partners-empty">Nos partenaires seront annoncés très bientôt — restez connectés !</p>
      ) : (
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {partners.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.08}>
              <article className="card-tactical flex h-full flex-col p-6 transition-transform hover:-translate-y-1"
                data-testid={`partner-card-${p.id}`}>
                <div className="mb-3 flex items-center justify-between gap-3">
                  <span className="rounded-md border border-orange-500/40 bg-orange-500/10 px-2 py-1 text-[11px] font-bold uppercase tracking-widest text-orange-400">
                    Partenaire
                  </span>
                  {p.logo_url && (
                    <img src={p.logo_url} alt={`Logo ${p.name}`} loading="lazy"
                      className="h-9 max-w-[100px] object-contain" />
                  )}
                </div>
                <h3 className="text-xl font-bold uppercase leading-tight tracking-tight">{p.name}</h3>
                {p.description && <p className="mt-3 flex-1 whitespace-pre-line text-sm leading-relaxed text-slate-400">{p.description}</p>}
                <div className="mt-4 flex items-center gap-2 text-xs font-bold uppercase tracking-widest">
                  {p.url ? (
                    <a href={p.url} target="_blank" rel="noreferrer" data-testid={`partner-link-${p.id}`}
                      className="inline-flex items-center gap-1.5 text-orange-400 transition-colors hover:text-orange-300">
                      <ExternalLink className="h-4 w-4" /> Visiter le site
                    </a>
                  ) : (
                    <span className="flex items-center gap-2 text-slate-500">
                      <Handshake className="h-4 w-4 text-orange-500" /> Partenaire officiel
                    </span>
                  )}
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      )}

      <Reveal delay={0.1}>
        <div className="card-tactical glow-orange relative mt-12 overflow-hidden p-8 sm:p-10" data-testid="partners-cta">
          <Handshake className="absolute -right-6 -top-6 h-40 w-40 text-orange-500/10" />
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500">Sponsoring</p>
          <h2 className="mt-2 max-w-xl text-2xl font-extrabold uppercase tracking-tight sm:text-3xl">
            Devenir partenaire d'Emisphere
          </h2>
          <p className="mt-3 max-w-xl text-sm text-slate-400">
            Vous êtes une marque et souhaitez associer votre image à une structure e-sport en pleine croissance ?
            Visibilité sur nos maillots, nos streams et nos réseaux : parlons-en.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/contact" data-testid="partners-cta-link"
              className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-6 py-3 font-heading text-lg font-bold uppercase tracking-wide text-[#060B18] transition-all hover:scale-[1.02] hover:bg-orange-400 active:scale-[0.98]">
              <Mail className="h-5 w-5" /> Nous contacter
            </Link>
            {mediaKit && (
              <a href={mediaKit.url} target="_blank" rel="noreferrer" download={mediaKit.filename || "media-kit-emisphere.pdf"}
                data-testid="partners-media-kit-btn"
                className="inline-flex items-center gap-2 rounded-xl border-2 border-orange-500 px-6 py-3 font-heading text-lg font-bold uppercase tracking-wide text-orange-400 transition-all hover:scale-[1.02] hover:bg-orange-500/10 active:scale-[0.98]">
                <FileDown className="h-5 w-5" /> Télécharger le media kit
              </a>
            )}
          </div>
        </div>
      </Reveal>
    </div>
  );
}
